import { P } from '@dnb/eufemia';
import { useDroppable } from '@dnd-kit/core';
import { horizontalListSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import classNames from 'classnames';

import { NavNode } from '../../utils';
import { NavigationItem } from './NavigationItem';
import { PlaceholderPage } from './PlaceholderPage';

interface Props {
  pinnedPages: NavNode[];
  recentPages: NavNode[];
}

export function PinnedPagesContainer({ pinnedPages, recentPages }: Props) {
  const { setNodeRef, active, isOver, over } = useDroppable({
    id: 'pinned-pages-container',
  });

  const overPinnedPageIndex = over && pinnedPages.findIndex((page) => page.path === over.id);
  const isOverPinnedArea = isOver || (overPinnedPageIndex !== null && overPinnedPageIndex !== -1);

  const recentPage = active && recentPages.find((page) => page.path === String(active.id));

  return (
    <div
      className={classNames(
        'flex items-center gap-4 p-2 bg-black-8 rounded-lg',
        active && [
          'outline-dashed',
          isOverPinnedArea ? 'bg-green-200 outline-green-400' : 'bg-blue-200 outline-blue-400',
        ],
      )}
      ref={setNodeRef}
    >
      {pinnedPages.length === 0 ? (
        <P className="whitespace-nowrap" size="small">
          Drag here to pin
        </P>
      ) : (
        <SortableContext items={pinnedPages.map((page) => page.path)} strategy={horizontalListSortingStrategy}>
          {/* Shows a placeholder to indicate where the recent page will be added between or in front of the other pinned pages */}
          {pinnedPages.map((page, index) => [
            recentPage && isOverPinnedArea && index === overPinnedPageIndex && (
              <PlaceholderPage key={`placeholder-${page.path}`} page={recentPage} />
            ),
            <PinnedPageItem key={page.path} node={page} />,
          ])}
          {/* Shows a placeholder to indicate where the recent page will be added to the end of the other pinned pages */}
          {recentPage && isOverPinnedArea && overPinnedPageIndex === -1 && <PlaceholderPage page={recentPage} />}
        </SortableContext>
      )}
    </div>
  );
}

function PinnedPageItem({ node }: { node: NavNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: node.path,
  });
  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1, // Hide original item when dragging as DragOverlay will show a copy
  };

  return (
    <div ref={setNodeRef} {...listeners} {...attributes} style={style}>
      <NavigationItem navNode={node} />
    </div>
  );
}
